import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import PublicCouponBanner from '../../components/PublicCouponBanner';
import '../../pcos-management-style.css';
import { apiGet, apiPost } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useNotification } from '../../context/CustomDialogContext';
import { useLeadModal } from '../../context/LeadModalContext';
import CountryPhoneInput from '../../components/CountryPhoneInput';
import CourseEnrollModal from '../../components/CourseEnrollModal';
import courseBgImage from '../../assets/images/courses/course_bg.webp';
import heroCourseImage from '../../assets/images/courses/diet-and-nutrition.webp';

/* ─── Accordion Item (Curriculum & FAQ) ───────────────────────────────────── */
const AccordionItem = ({ num, title, subtitle, children, isActive, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');

  useEffect(() => {
    setHeight(isActive ? `${contentRef.current ? contentRef.current.scrollHeight : 500}px` : '0px');
  }, [isActive]);

  return (
    <div className={`fy-accordion-item ${isActive ? 'active' : ''}`}>
      <button
        className="fy-accordion-btn"
        onClick={onToggle}
        aria-expanded={isActive}
        type="button"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <span className="fy-accordion-num">{num}</span>
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 700 }}>{title}</span>
            {subtitle && <span className="fy-accordion-subtitle">{subtitle}</span>}
          </span>
        </span>
        <div className="fy-accordion-icon"><i className="bi bi-plus"></i></div>
      </button>
      <div className="fy-accordion-content" style={{ maxHeight: height }}>
        <div ref={contentRef} className="fy-accordion-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const PCOSManagementProgram = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCountry, formatPrice } = useCountry();
  const { showAlert } = useNotification();
  const { openModal } = useLeadModal();

  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

  const courseTitle = "3-Month PCOS & PCOD Management Program";

  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  const [activeModule, setActiveModule] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    father_name: '',
    address: '',
    countryCode: '+91',
    phone: '',
    email: '',
    batch_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [dbCourse, setDbCourse] = useState(null);
  const [demoInfo, setDemoInfo] = useState(null);
  const [upcomingBatches, setUpcomingBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    async function fetchCoupons() {
      try {
        let couponsRes = await apiGet('/public-banner-coupons.php');
        if (!couponsRes || (Array.isArray(couponsRes) && couponsRes.length === 0) || (couponsRes.data && couponsRes.data.length === 0)) {
          couponsRes = await apiGet('/public-coupons.php');
        }
        let list = [];
        if (Array.isArray(couponsRes)) {
          list = couponsRes;
        } else if (couponsRes && Array.isArray(couponsRes.data)) {
          list = couponsRes.data;
        } else if (couponsRes && Array.isArray(couponsRes.list)) {
          list = couponsRes.list;
        }
        setActiveCoupons(list);
      } catch (err) {
        console.error('Failed to fetch coupons:', err);
      }
    }
    fetchCoupons();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const parseDateSafe = (dateStr) => {
    if (!dateStr) return null;
    let cleanStr = String(dateStr).trim();
    if (!cleanStr) return null;

    let d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    cleanStr = cleanStr.replace('T', ' ').replace(/-/g, '/');
    d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    d = new Date(cleanStr.replace(/ /g, 'T'));
    if (!isNaN(d.getTime())) return d;

    return null;
  };

  const getBannerCouponForPcos = () => {
    if (!dbCourse) return null;

    const match = activeCoupons.find(
      (c) => Number(c.course_id) === Number(dbCourse.id) || Number(c.course_id) === 0
    );
    if (!match) return null;

    // Check status
    if (match.status && match.status !== 'active') return null;

    // Check show_on_pages flag (controlled by admin checkbox "Show this coupon code on program pages and individual course pages")
    const isShowOnPages = match.show_on_pages === undefined || match.show_on_pages === null || match.show_on_pages === 1 || match.show_on_pages === '1' || match.show_on_pages === true;
    if (!isShowOnPages) return null;

    const now = currentTime.getTime();

    if (match.banner_expiry) {
      const expDate = parseDateSafe(match.banner_expiry);
      if (expDate && expDate.getTime() - now <= 0) {
        return null;
      }
    }
    if (match.banner_start) {
      const startDate = parseDateSafe(match.banner_start);
      if (startDate && now < startDate.getTime()) {
        return null;
      }
    }
    if (match.expiry_date) {
      const expDate = parseDateSafe(match.expiry_date);
      if (expDate && expDate.getTime() - now <= 0) {
        return null;
      }
    }

    return match;
  };

  const formatCountdown = (expiryDateStr) => {
    const expDate = parseDateSafe(expiryDateStr);
    if (!expDate) return '';

    const difference = expDate.getTime() - currentTime.getTime();
    if (difference <= 0) return 'Expired';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    parts.push(`${hours} hr`);
    parts.push(`${minutes} min`);
    parts.push(`${seconds} sec`);

    return parts.join(' ');
  };

  /* Set body class for scoped CSS immediately before paint */
  useLayoutEffect(() => {
    document.body.classList.add('pcos-page');
    return () => { document.body.classList.remove('pcos-page'); };
  }, []);

  /* Load course & batches & demo class availability */
  useEffect(() => {
    async function loadData() {
      try {
        const coursesData = await apiGet('/public-courses.php');
        let currentCourseId = 0;
        let targetTitle = courseTitle;
        if (coursesData && Array.isArray(coursesData)) {
          let match = coursesData.find(
            c => c.title && c.title.toLowerCase().trim() === courseTitle.toLowerCase().trim()
          );
          if (!match) {
            match = coursesData.find(
              c => (c.slug && c.slug.toLowerCase().trim() === 'pcos-pcod-management-program') ||
                   (c.course_slug && c.course_slug.toLowerCase().trim() === 'pcos-pcod-management-program') ||
                   (c.title && c.title.toLowerCase().includes('pcos'))
            );
          }
          if (match) {
            setDbCourse(match);
            currentCourseId = match.id;
            if (match.title) {
              targetTitle = match.title;
            }
          }
        }

        // Query upcoming batches using matched dbCourse title first
        let batchesData = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(targetTitle)}`);
        
        // Fallback to static courseTitle if different and no batches returned
        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && targetTitle !== courseTitle) {
          const fallbackBatches = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(courseTitle)}`);
          if (fallbackBatches && Array.isArray(fallbackBatches) && fallbackBatches.length > 0) {
            batchesData = fallbackBatches;
          }
        }

        // Fallback to course_id if still no batches found
        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && currentCourseId > 0) {
          const idBatches = await apiGet(`/get-upcoming-batches.php?course_id=${currentCourseId}`);
          if (idBatches && Array.isArray(idBatches) && idBatches.length > 0) {
            batchesData = idBatches;
          }
        }

        setUpcomingBatches(batchesData || []);

        try {
          if (currentCourseId > 0) {
            const demoRes = await apiGet(`/public/check-demo-course.php?course_id=${currentCourseId}&slug=pcos-pcod-management-program`);
            if (demoRes && demoRes.available) {
              setDemoInfo(demoRes);
            }
          }
        } catch (err) {
          console.log("No demo configuration for this course.");
        }
      } catch (err) {
        console.error('Failed to load course details & batches:', err);
      } finally {
        setLoadingBatches(false);
      }
    }
    loadData();
  }, []);

  /* Sync logged-in user data */
  useEffect(() => {
    if (user && user.role === 'student') {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        countryCode: user.country_code || '+91',
        phone: user.mobile_number || user.phone || '',
        father_name: user.father_name || '',
        address: user.address || ''
      }));
    }
  }, [user]);

  /* SEO & scroll */
  useEffect(() => {
    document.title = "3-Month PCOS & PCOD Management Program Online | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content',
        "Join PranaFlow Academy's structured 3-month online PCOS & PCOD management program with nutrition education, yoga, exercise, stress management, sleep support, menstrual health education, student dashboard access and installment payment options.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/courses/pcos-pcod-management-program");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "3-Month PCOS & PCOD Management Program Online | PranaFlow Academy" },
      { property: 'og:description', content: "Join PranaFlow Academy's structured 3-month online PCOS & PCOD management program with nutrition education, yoga, exercise, stress management, sleep support, menstrual health education, student dashboard access and installment payment options." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/courses/pcos-pcod-management-program" },
      { property: 'og:image', content: "https://pranaflowacademy.com/images/courses/diet-and-nutrition.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter / X Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "3-Month PCOS & PCOD Management Program Online | PranaFlow Academy" },
      { name: 'twitter:description', content: "Join PranaFlow Academy's structured 3-month online PCOS & PCOD management program with nutrition education, yoga, exercise, stress management, sleep support, menstrual health education, student dashboard access and installment payment options." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/images/courses/diet-and-nutrition.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  /* Schema injection */
  useEffect(() => {
    const scriptId = 'jsonld-schema-pcosmanagement';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const courseSchema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": "https://pranaflowacademy.com/courses/pcos-pcod-management-program#course",
      "name": "3-Month PCOS & PCOD Management Program",
      "description": "Join PranaFlow Academy's structured 3-month online PCOS & PCOD management program covering nutrition education, yoga, exercise, stress management, sleep support, and menstrual health education.",
      "url": "https://pranaflowacademy.com/courses/pcos-pcod-management-program",
      "provider": {
        "@id": "https://pranaflowacademy.com/#organization"
      },
      "courseMode": "online",
      "educationalCredentialAwarded": "Certificate of Completion",
      "inLanguage": "en"
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pranaflowacademy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Courses",
          "item": "https://pranaflowacademy.com/courses"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "3-Month PCOS & PCOD Management Program",
          "item": "https://pranaflowacademy.com/courses/pcos-pcod-management-program"
        }
      ]
    };

    script.textContent = JSON.stringify([courseSchema, breadcrumbSchema]);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) existingScript.remove();
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { showAlert({ title: "Warning", message: "Student Name is required.", type: "warning" }); return; }
    if (!formData.email.trim()) { showAlert({ title: "Warning", message: "Email address is required.", type: "warning" }); return; }
    if (!formData.countryCode.trim()) { showAlert({ title: "Warning", message: "Country Code is required.", type: "warning" }); return; }
    if (!formData.phone.trim()) { showAlert({ title: "Warning", message: "Please enter your mobile number.", type: "warning" }); return; }

    const isInactive = dbCourse && dbCourse.status === 'inactive';

    if (isInactive || upcomingBatches.length === 0) {
      setIsSubmitting(true);
      try {
        const payload = {
          name: formData.name.trim(),
          father_name: formData.father_name ? formData.father_name.trim() : null,
          mobile: formData.phone.trim(),
          country_code: formData.countryCode.trim(),
          mobile_number: formData.phone.trim(),
          country_iso_code: selectedCountry ? selectedCountry.country_code : 'IN',
          email: formData.email.trim(),
          address: formData.address ? formData.address.trim() : null,
          course_id: dbCourse ? dbCourse.id : null,
          country: selectedCountry ? selectedCountry.country_name : null,
          message: "I am interested in the 3-Month PCOS & PCOD Management Program. Please contact me when new batches are available.",
          source_page: window.location.pathname
        };
        await apiPost('/save-interested-lead.php', payload);
        setIsSuccess(true);
      } catch (err) {
        showAlert({ title: "Error", message: err.message || "Failed to submit details. Please try again.", type: "error" });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!formData.father_name.trim()) { showAlert({ title: "Warning", message: "Father's / Guardian's Name is required.", type: "warning" }); return; }
    if (!formData.address.trim()) { showAlert({ title: "Warning", message: "Full Address is required.", type: "warning" }); return; }
    if (!formData.batch_id) { showAlert({ title: "Warning", message: "Please select an upcoming batch.", type: "warning" }); return; }

    const selectedBatch = upcomingBatches.find(b => Number(b.id) === Number(formData.batch_id));
    if (!selectedBatch) { showAlert({ title: "Warning", message: "Selected batch not found.", type: "warning" }); return; }

    navigate('/enroll-confirm', {
      state: {
        studentDetails: {
          name: formData.name, father_name: formData.father_name,
          address: formData.address, countryCode: formData.countryCode,
          phone: formData.phone, email: formData.email
        },
        courseDetails: {
          id: dbCourse ? dbCourse.id : null,
          title: courseTitle,
          duration: dbCourse ? dbCourse.duration : '3 Months / 12 Weeks',
          fee_inr: dbCourse ? dbCourse.fee_inr : 40000,
          fee_usd: dbCourse ? dbCourse.fee_usd : 480
        },
        batchDetails: {
          id: selectedBatch.id, name: selectedBatch.batch_name,
          timing: selectedBatch.batch_timing, start_date: selectedBatch.start_date
        }
      }
    });
  };

  const currentFeeInr = dbCourse ? parseFloat(dbCourse.fee_inr) || 0 : 0;
  const displayPrice = dbCourse && currentFeeInr > 0 ? formatPrice(currentFeeInr, dbCourse) : (dbCourse ? 'Fee will be updated soon' : 'Loading...');

  /* ─── DATA ─────────────────────────────────────────────────────────────── */
  const pillars = [
    { num: "Pillar 01", title: "Understand Your PCOS", desc: "Learn what is happening in your body and understand PCOS beyond social-media myths.", tags: ["Hormones", "Insulin Resistance", "Symptoms", "Ovulation", "Menstrual Health"] },
    { num: "Pillar 02", title: "Eat With Confidence", desc: "Build sustainable eating habits rather than following another extreme PCOS diet.", tags: ["Nutrition", "Balanced Meals", "Meal Planning", "Cravings", "Indian Food"] },
    { num: "Pillar 03", title: "Move Your Body", desc: "Create a realistic movement routine that can fit smoothly into everyday life.", tags: ["Walking", "Cardio", "Strength", "Mobility", "Exercise Planning"] },
    { num: "Pillar 04", title: "Practice Yoga & Relaxation", desc: "Use guided movement and relaxation practices as part of overall wellbeing.", tags: ["Yoga", "Pranayama", "Breathing", "Relaxation", "Mind-Body"] },
    { num: "Pillar 05", title: "Improve Your Lifestyle", desc: "Work on the lifestyle factors often ignored when people focus only on dieting.", tags: ["Sleep", "Stress", "Emotional Wellbeing", "Daily Routine", "Recovery"] },
    { num: "Pillar 06", title: "Build Your Long-Term Plan", desc: "Finish with a personal system you can continue after the 12 weeks are complete.", tags: ["Cycle Tracking", "Progress Reviews", "Fertility Awareness", "Habit Maintenance"] },
  ];

  const weeklyModules = [
    { num: "Module 01", title: "Understand Your PCOS & PCOD", topics: ["Definitions, root causes & diagnosis criteria", "Understanding hormonal imbalances & androgen excess", "Role of insulin resistance & chronic low-grade inflammation", "Symptom mapping & baseline progress tracking"] },
    { num: "Module 02", title: "Nutrition Fundamentals for PCOS", topics: ["Macro & micronutrient requirements for metabolic health", "Blood sugar management & glycaemic load principles", "Role of dietary fibre, healthy fats & protein quality", "Navigating Indian food groups without extreme restrictions"] },
    { num: "Module 03", title: "Practical Meal Planning & Grocery Strategy", topics: ["Building balanced Indian plates (Breakfast, Lunch, Dinner)", "Smart grocery planning & ingredient label reading", "Healthy snack options & managing mid-day energy crashes", "Managing food cravings & eating out realistically"] },
    { num: "Module 04", title: "Metabolic Health & Month 1 Progress Review", topics: ["Evaluating insulin sensitivity progress & habit consistency", "Reviewing food diaries & digestive health improvements", "Refining nutritional strategies for busy work schedules", "Month 1 Progress Assessment & milestone check-in"] },
    { num: "Module 05", title: "Exercise & Physical Activity Education", topics: ["Role of physical activity in insulin sensitivity", "Cardio vs. resistance training for PCOS management", "Building a realistic movement routine at home or gym", "Preventing over-training & managing fatigue"] },
    { num: "Module 06", title: "Therapeutic Yoga, Asana & Mobility", topics: ["PCOS-friendly yoga practices & pelvis mobility routines", "Asana practice for circulation, posture & abdominal health", "Restorative postures for stress release & nervous system balance", "Adapting practices during menstruation & low-energy days"] },
    { num: "Module 07", title: "Stress Management, Cortisol & Emotional Wellbeing", topics: ["Understanding the HPA axis & stress-PCOS connection", "Pranayama & breathwork techniques for nervous system calm", "Addressing emotional eating, anxiety & body-image concerns", "Daily stress-reduction rituals for work & home life"] },
    { num: "Module 08", title: "Sleep Optimization & Month 2 Review", topics: ["Circadian rhythm, melatonin & sleep quality for hormones", "Designing an effective wind-down & night routine", "Overcoming sleep disturbances & morning tiredness", "Month 2 Progress Review & routine adjustments"] },
    { num: "Module 09", title: "Menstrual Cycle & Reproductive Health Education", topics: ["Understanding the normal cycle vs. irregular PCOS cycles", "Tracking ovulation indicators & cervical mucus signs", "Cycle-syncing lifestyle adjustments for work & exercise", "Managing PMS, cramping & mood changes naturally"] },
    { num: "Module 10", title: "Common PCOS Concerns & Self-Care", topics: ["Understanding acne, hirsutism (facial hair) & hair thinning", "Skin care & scalp care principles supporting hormonal health", "Navigating social situations, travel & holiday periods", "Building resilience against all-or-nothing thinking"] },
    { num: "Module 11", title: "Fertility Awareness, Medical Tests & Supplements", topics: ["Evidence-based clarity on PCOS fertility & preconception care", "Understanding key blood tests (HbA1c, Lipid Profile, Thyroid, Hormones)", "Role of dietary supplements (Inositol, Vitamin D, Omega-3, B12)", "Working effectively with your treating doctor & specialist"] },
    { num: "Module 12", title: "Long-Term Management Plan & Final Deliverable", topics: ["Reviewing complete 12-week progress & habit milestones", "Designing your personal long-term lifestyle management plan", "Handing setbacks, busy periods & maintaining consistency", "Final Q&A, graduation review & student dashboard exit strategy"] }
  ];

  const painPoints = [
    { title: "Irregular or missed periods", desc: "Unpredictable cycle length or heavy flow concerns" },
    { title: "Difficulty managing weight", desc: "Weight loss struggles or sudden unwanted weight gain" },
    { title: "Acne or oily skin", desc: "Persistent facial breakouts and hormonal skin changes" },
    { title: "Facial or body hair (Hirsutism)", desc: "Androgen-related hair growth concerns" },
    { title: "Hair thinning", desc: "Scalp hair thinning or changes in hair texture" },
    { title: "Cravings & low energy", desc: "Intense sugar cravings and afternoon energy crashes" },
    { title: "Stress & emotional eating", desc: "High daily stress, anxiety, and emotional triggers" },
    { title: "Sleep problems", desc: "Difficulty falling asleep or non-restorative sleep" },
    { title: "Fertility concerns", desc: "Uncertainty regarding ovulation and cycle tracking" },
    { title: "Food & exercise confusion", desc: "Confused about what to eat and which exercises are safe" },
    { title: "Trying multiple diets", desc: "Dieting repeatedly without building sustainable habits" },
    { title: "Lack of accountability", desc: "Struggling to maintain routines without structured support" },
  ];

  const highlights = [
    "Up to 72 Guided Live Online Sessions",
    "Complete 12 Structured Weekly Modules",
    "Nutrition Education & Practical Meal Planning",
    "Therapeutic Yoga, Pranayama & Mobility Practices",
    "Exercise & Physical Activity Education",
    "Stress-Management & Sleep Optimization Routines",
    "Menstrual-Health & Fertility Awareness Education",
    "Monthly Progress Reviews & Milestone Tracking",
    "Ask-the-Expert Specialist Sessions",
    "PranaFlow Student Dashboard Access",
    "Class Recording Catch-Up & Revision Access",
    "Downloadable Program Trackers & Worksheets",
    "Personal Long-Term Lifestyle Management Plan",
  ];

  const bonuses = [
    "PCOS Wellness Assessment",
    "12-Week Progress Tracker",
    "Menstrual Cycle Tracker",
    "Symptom Tracker",
    "Weekly Meal Planner",
    "Grocery Planning Guide",
    "Personal PCOS Management Plan",
  ];

  const myths = [
    { m: '"PCOS means ovarian cysts."', f: 'The follicles seen on ultrasound are immature fluid-filled sacs, not true cysts. Diagnosis involves ovulation and hormonal patterns, not cysts alone.' },
    { m: '"Women with PCOS cannot eat carbohydrates."', f: 'Carbs provide vital energy. Choosing high-fibre, low-GI whole grains and balanced Indian meals supports insulin sensitivity without zero-carb diets.' },
    { m: '"Everyone with PCOS must lose weight."', f: 'Lean PCOS exists in women who are not overweight. Metabolic health, ovulation, and symptom care matter for all body types.' },
    { m: '"Fruit & Dairy must always be eliminated."', f: 'Unnecessary food elimination often creates nutritional gaps. Whole fruits and dairy can fit into a balanced plan unless specific medical intolerance exists.' },
    { m: '"One supplement can fix PCOS."', f: 'No single supplement replaces consistent nutrition, physical activity, yoga, stress reduction, and healthy sleep.' },
    { m: '"One specific yoga pose cures PCOS."', f: 'Yoga is a holistic practice. Mobility, breathwork, relaxation, and overall movement work together to support wellbeing, not one isolated pose.' },
  ];

  const diffPoints = [
    { title: "Complete 3-Month Structure", desc: "A guided 12-week progression rather than disconnected single classes." },
    { title: "Multiple Areas of Management", desc: "Nutrition + Exercise + Yoga + Stress + Sleep + Menstrual Health + Long-Term Plan." },
    { title: "Practical Learning", desc: "Learn how to build meals, plan groceries, handle cravings, exercise, and track cycles." },
    { title: "PranaFlow Student Dashboard", desc: "Enrolled course content, schedule, and uploaded recordings organized in one portal." },
    { title: "Missed-Class Support", desc: "Uploaded lecture recordings allow you to catch up when work or travel happens." },
    { title: "Revision Access", desc: "Revisit previous lectures during your access period to reinforce learning." },
    { title: "Progress Reviews", desc: "Periodic reviews to evaluate consistency, symptoms, and upcoming goals." },
    { title: "Designed for Real Life", desc: "Accounts for work, college, family, travel, festivals, and busy schedules." },
  ];

  const faqs = [
    { q: "How long is the program?", a: "The program runs for approximately 3 months / 12 weeks." },
    { q: "How many classes are included?", a: "The schedule is designed around six teaching days per week with approximately one-hour sessions, providing up to approximately 72 guided sessions across the complete program." },
    { q: "What if I miss a class?", a: "Available lecture recordings can be viewed through the PranaFlow Academy Student Dashboard according to the existing LMS recording and access setup." },
    { q: "Can I revise previous lectures?", a: "Yes, available previous lecture videos can be revisited during the applicable access period." },
    { q: "Will I receive a Student Dashboard?", a: "Yes, after successful enrollment according to the existing PranaFlow Academy LMS account and enrollment flow." },
    { q: "Is this only a yoga program?", a: "No. It covers PCOS education, nutrition, meal planning, movement, yoga, stress, sleep, menstrual health, symptoms, fertility awareness, habits and long-term management." },
    { q: "Can I pay in installments?", a: "Yes, according to the installment plans configured for the course." },
    { q: "Is it suitable for beginners?", a: "Yes. The program includes beginner-friendly practices and appropriate modifications." },
    { q: "Does this cure or reverse PCOS?", a: "No. No guaranteed cure or reversal claim is made. The program focuses on lifestyle education and sustainable healthy habits supporting overall PCOS management. Individual experiences vary." },
    { q: "Should I stop my prescription medication?", a: "No. Students should continue appropriate medical care and discuss medication decisions with their treating healthcare professional." },
  ];

  /* ─── JSX ─────────────────────────────────────────────────────────────── */
  return (
    <div className="pcos-page public-site">
      <PublicHeader />

      <main className="main">
        <div className="fy-header-spacer"></div>

        {/* 1. HERO SECTION */}
        <section id="hero" className="fy-section">
          <div className="fy-hero-container">
            <div className="fy-hero-wrapper">
              <div className="row align-items-center gy-4">
                <div className="col-lg-6">
                  <span className="fy-hero-pill">
                    <i className="bi bi-star-fill text-warning me-1"></i> 3-Month Live Online Wellness Program
                  </span>
                  <h1 className="fy-hero-title">3-Month PCOS &amp; PCOD Management Program</h1>
                  <div className="fy-hero-subhead">Take Control of Your PCOS Journey — One Healthy Habit at a Time</div>

                  <p className="fy-hero-para">
                    Living with PCOS or PCOD can feel confusing. Irregular periods, weight-management struggles, acne, hair concerns, cravings, low energy, and fertility worries leave many women overwhelmed. You do not need another random PCOS tip. <strong>You need a structured plan.</strong>
                  </p>

                  <p className="fy-hero-para">
                    Our structured 3-month program covers nutrition education, yoga, exercise, stress management, sleep support, menstrual-health education, and regular progress reviews — all managed through your PranaFlow Student Dashboard.
                  </p>

                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                    {demoInfo && demoInfo.available && (
                      <button 
                        type="button"
                        className="fy-btn-secondary" 
                        onClick={() => navigate(`/demo-class?course=${demoInfo.courseSlug || 'pcos-pcod-management-program'}&course_id=${dbCourse ? dbCourse.id : ''}&demo_course_id=${demoInfo.demoCourseId}`, { state: { course: dbCourse } })}
                      >
                        {demoInfo.demoFee > 0 
                          ? `Demo Class — ₹${Number(demoInfo.demoFee)}` 
                          : 'Demo Class'}
                      </button>
                    )}
                  </div>

                  <div className="fy-trust-badges">
                    <span><i className="bi bi-clock-fill"></i> 3 Months / 12 Weeks</span>
                    <span><i className="bi bi-laptop"></i> Online Guided Sessions</span>
                    <span><i className="bi bi-collection-play"></i> Up to 72 Sessions</span>
                    <span><i className="bi bi-speedometer2"></i> Student Dashboard</span>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-hero-img-container">
                    <img src={heroCourseImage} alt="PCOS & PCOD Management Program" loading="eager" />
                  </div>
                </div>
              </div>

              {/* Feature Callout Strip */}
              <div className="fy-strip-container">
                <div className="fy-strip-grid">
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-calendar3"></i></div>
                    <div className="fy-strip-label">3 Months<br />12 Weeks</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-camera-video-fill"></i></div>
                    <div className="fy-strip-label">Live Online<br />Sessions</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-journal-bookmark"></i></div>
                    <div className="fy-strip-label">12<br />Modules</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-collection-play"></i></div>
                    <div className="fy-strip-label">Up to 72<br />Sessions</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-speedometer2"></i></div>
                    <div className="fy-strip-label">Student<br />Dashboard</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-credit-card-2-back"></i></div>
                    <div className="fy-strip-label">Installment<br />Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. OVERVIEW — "A comprehensive program designed for real results" */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-plan-box">
              <h2 className="fy-serif-heading mb-4 text-center text-lg-start">A comprehensive program designed for real results</h2>

              <div className="row gy-4 align-items-center">
                <div className="col-lg-6">
                  <p className="fy-hero-para">
                    You do not need years of health knowledge to begin. Whether you want to understand PCOS better, build sustainable lifestyle habits, or find practical guidance across nutrition, movement, yoga, sleep, and stress — this structured three-month online program gives you the tools to begin with confidence.
                  </p>
                  <p className="fy-hero-para">
                    With this program, you receive expert guidance through six teaching days per week, structured weekly modules, class recordings for catch-up, monthly progress reviews, and access to your personal PranaFlow Student Dashboard.
                  </p>

                  <div className="fy-disclaimer-box">
                    <i className="bi bi-info-circle-fill"></i>
                    <div>
                      This program is educational and does not diagnose, treat, or cure PCOS or any medical condition. Continue prescribed medical care and consult your healthcare professional before making lifestyle changes.
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Nutrition education &amp; practical meal planning</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Therapeutic yoga, pranayama &amp; mobility practices</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Exercise &amp; physical activity education</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Stress management &amp; sleep optimization</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Menstrual-health &amp; fertility awareness education</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Student Dashboard access</span></div>
                  <div className="fy-benefit-pill mb-0"><i className="bi bi-check-circle-fill"></i><span>Class recording catch-up &amp; revision access</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. HIGHLIGHTS & BONUS — "What You Will Cover" */}
        <section id="highlights" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">What You Will Cover</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-stretch">
              <div className="col-lg-8">
                <div className="fy-highlights-grid">
                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>PCOS &amp; PCOD Education — Hormones, Symptoms, Insulin Resistance</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Nutrition Fundamentals &amp; Balanced Meal Building</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Practical Indian Meal Planning &amp; Grocery Guidance</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Metabolic Health &amp; Managing Insulin Resistance</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Exercise, Strength &amp; Daily Movement Routine</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Yoga, Pranayama &amp; Restorative Relaxation</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Stress Management &amp; Emotional Eating Support</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Sleep Optimization &amp; Healthy Daily Routine</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Menstrual Cycle &amp; Reproductive Health Education</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>PCOS Myth-Busting &amp; Evidence-Based Clarity</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Common PCOS Concerns — Acne, Hair, Fatigue, Cravings</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Fertility, Medical Tests &amp; Supplement Education</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Long-Term Lifestyle Management Plan</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Monthly Progress Reviews &amp; Milestone Tracking</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Ask-the-Expert Specialist Sessions</span></div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="fy-bonus-card">
                  <div className="fy-bonus-icon"><i className="bi bi-gift-fill"></i></div>
                  <div className="fy-bonus-title">INCLUDED RESOURCES</div>
                  <div className="fy-bonus-sub">Digital Worksheets &amp; Trackers</div>
                  <ul className="fy-bonus-list">
                    {bonuses.map((b, i) => (
                      <li key={i}><i className="bi bi-check2-circle"></i>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. CURRICULUM OVERVIEW — 6 Pillars & 12 Weekly Modules Accordion */}
        <section id="curriculum" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Program Overview — 6 Core Pillars</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-curriculum-grid mb-4">
              {pillars.map((p, i) => (
                <div key={i} className="fy-week-card">
                  <div className="fy-week-icon"><i className="bi bi-flower2"></i></div>
                  <span className={`fy-week-badge ${i % 2 === 0 ? 'sage' : 'blush'}`}>{p.num}</span>
                  <h3 className="fy-week-title">{p.title}</h3>
                  <ul className="fy-week-list">
                    {p.tags.map((t, j) => <li key={j}>{t}</li>)}
                  </ul>
                  <p style={{ fontSize: '0.85rem', color: 'var(--fy-text-muted)', marginTop: '10px', lineHeight: 1.5 }}>{p.desc}</p>
                </div>
              ))}
            </div>

            {/* 12-Module Syllabus Accordion */}
            <div className="fy-section-title-wrap mt-5 mb-4">
              <h3 className="fy-serif-heading" style={{ fontSize: '1.6rem' }}>Complete 12-Module Detailed Syllabus</h3>
              <p className="fy-hero-para" style={{ maxWidth: '650px', margin: '8px auto 0' }}>
                Click on any module to view the topics covered in the 3-month curriculum.
              </p>
            </div>

            <div className="fy-syllabus-container">
              {weeklyModules.map((m, idx) => (
                <AccordionItem
                  key={idx}
                  num={m.num}
                  title={m.title}
                  isActive={activeModule === idx}
                  onToggle={() => setActiveModule(activeModule === idx ? null : idx)}
                >
                  <h5 style={{ fontWeight: 700, color: 'var(--fy-sage-dark)', fontSize: '0.92rem', marginBottom: '10px' }}>Topics Covered:</h5>
                  <ul style={{ paddingLeft: '20px', color: 'var(--fy-text-muted)', fontSize: '0.88rem', lineHeight: 1.65, margin: 0 }}>
                    {m.topics.map((top, tIdx) => (
                      <li key={tIdx} style={{ marginBottom: '6px' }}>{top}</li>
                    ))}
                  </ul>
                </AccordionItem>
              ))}
            </div>
          </div>
        </section>

        {/* 5. COURSE DETAILS / WHO IT IS FOR & PRICING CARD */}
        <section id="course-details" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="row gy-5 align-items-stretch">
              {/* Left image */}
              <div className="col-lg-4" style={{ textAlign: 'center' }}>
                <div className="fy-business-image-wrap">
                  <img src={courseBgImage} alt="PCOS Program" className="img-fluid w-100" />
                </div>
              </div>

              {/* Center — Who it is for */}
              <div className="col-lg-4">
                <h2 className="fy-serif-heading mb-3">Does This Sound Like You?</h2>
                <div className="text-muted fw-bold mb-3" style={{ fontSize: '0.95rem' }}>Common reasons women join this program.</div>

                <p className="fy-hero-para">
                  Women who feel overwhelmed by conflicting online PCOS advice, struggle to maintain sustainable habits, or want structured expert guidance across nutrition, movement, yoga, sleep, and menstrual health.
                </p>

                <div className="d-flex flex-column gap-2 mb-3">
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Know I need to improve lifestyle but don't know where to start</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Tired of conflicting PCOS advice from social media</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Want to understand nutrition without extreme dieting</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Need structure, accountability, and expert guidance</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Want to build habits that fit my real daily schedule</span></div>
                  <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Concerned about hormonal health, periods, or fertility</span></div>
                </div>

                <div className="small text-muted fst-italic">
                  *PranaFlow Academy does not guarantee any specific health outcome or reversal of PCOS. Individual experiences vary. This program is educational in nature.
                </div>
              </div>

              {/* Right — Pricing card V2 */}
              <div className="col-lg-4">
                {(() => {
                  const coupon = getBannerCouponForPcos();
                  const feeInr = dbCourse ? parseFloat(dbCourse.fee_inr) : 0;
                  let formattedOriginalPrice = displayPrice;
                  let formattedDiscountedPrice = '';
                  let percentageVal = 0;

                  if (coupon && feeInr > 0) {
                    if (coupon.discount_type === 'percentage') {
                      percentageVal = Math.round(parseFloat(coupon.discount_value));
                      const discountedInr = feeInr * (1 - percentageVal / 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    } else if (coupon.discount_type === 'flat' || coupon.discount_type === 'fixed') {
                      const fixedVal = parseFloat(coupon.discount_value);
                      const discountedInr = Math.max(0, feeInr - fixedVal);
                      percentageVal = Math.round((fixedVal / feeInr) * 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    }
                  }

                  return (
                    <div className="fy-program-details-card-v2" id="pricing">
                      <h3 className="pcos-pd-heading">PROGRAM DETAILS</h3>
                      <div className="pcos-pd-heading-divider"></div>

                      <div className="pcos-pd-rows-wrap">
                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-calendar-event"></i>
                            <span>Duration</span>
                          </div>
                          <div className="pcos-pd-val">3 Months / 12 Weeks</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-laptop"></i>
                            <span>Mode</span>
                          </div>
                          <div className="pcos-pd-val">Online Guided Sessions</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-collection-play"></i>
                            <span>Classes</span>
                          </div>
                          <div className="pcos-pd-val">Up to 72 Sessions</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-calendar-week"></i>
                            <span>Schedule</span>
                          </div>
                          <div className="pcos-pd-val">6 Days/Week · ~1 Hr/Day</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-speedometer2"></i>
                            <span>Portal</span>
                          </div>
                          <div className="pcos-pd-val">PranaFlow Dashboard</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-credit-card-2-back"></i>
                            <span>Payment</span>
                          </div>
                          <div className="pcos-pd-val">Full or Installments</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-person-check"></i>
                            <span>Level</span>
                          </div>
                          <div className="pcos-pd-val">Beginners Welcome</div>
                        </div>
                      </div>

                      <div className="pf-course-promo-badge pf-course-promo-badge-pd">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                          <line x1="7" y1="7" x2="7.01" y2="7"></line>
                        </svg>
                        <span>Every batch: First 5 students get an extra discount on fee!</span>
                      </div>

                      {coupon ? (
                        <div className="pcos-pd-offer-box">
                          <div className="pcos-pd-offer-pill">
                            <i className="bi bi-star"></i> LIMITED TIME OFFER
                          </div>

                          <div className="pcos-pd-offer-prices-row">
                            <div className="pcos-pd-orig-price-wrap">
                              <span className="pcos-pd-metric-label">ORIGINAL FEE</span>
                              <span className="pcos-pd-orig-price">{formattedOriginalPrice}</span>
                            </div>

                            <div className="pcos-pd-disc-price-wrap">
                              <span className="pcos-pd-metric-label">DISCOUNTED FEE</span>
                              <span className="pcos-pd-disc-price">{formattedDiscountedPrice}</span>
                            </div>

                            <div className="pcos-pd-badge-wrap">
                              <div className="pcos-pd-badge-rect">
                                <span className="pcos-pd-badge-num">{percentageVal}%</span>
                                <span className="pcos-pd-badge-text">OFF</span>
                              </div>
                            </div>
                          </div>

                          <div className="pcos-pd-timer-strip">
                            <i className="bi bi-stopwatch"></i>
                            <span>
                              {formatCountdown(coupon.banner_expiry || coupon.expiry_date).includes('day') ? 'Offer ends in' : 'Offer valid today'} · {formatCountdown(coupon.banner_expiry || coupon.expiry_date)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="pcos-pd-normal-box">
                          <span className="pcos-pd-normal-label">TUITION FEE</span>
                          <span className="pcos-pd-normal-price">{loadingBatches ? 'Loading...' : displayPrice}</span>
                        </div>
                      )}

                      <button
                        type="button"
                        className="pcos-pd-enroll-btn"
                        onClick={() => setIsEnrollModalOpen(true)}
                      >
                        <span>Enroll Now &amp; Reserve Your Seat</span>
                        <div className="pcos-pd-btn-arrow-circle">
                          <i className="bi bi-arrow-right"></i>
                        </div>
                      </button>

                      <button
                        type="button"
                        className="fy-btn-secondary w-100 mt-2"
                        onClick={() => openModal(courseTitle, dbCourse)}
                        style={{
                          borderRadius: '30px',
                          padding: '12px 20px',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        Enquire Now <i className="bi bi-chat-dots"></i>
                      </button>

                      <div className="pcos-pd-next-batch-strip">
                        <i className="bi bi-leaf"></i>
                        <span>Next batch admissions open — inquire now</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* 6. PCOS SYMPTOMS — "Pain Points" */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">You Don't Have to Navigate PCOS Alone</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '700px', margin: '12px auto 0' }}>
                We understand the daily challenges women face when trying to manage PCOS independently.
              </p>
            </div>

            <div className="fy-pain-grid">
              {painPoints.map((p, i) => (
                <div key={i} className="fy-pain-card">
                  <span className="fy-pain-x">✕</span>
                  <div>
                    <h4 className="fy-pain-title">{p.title}</h4>
                    <span className="fy-pain-desc">{p.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: 'var(--fy-cream-card)', border: '2px dashed var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 32px', textAlign: 'center', marginTop: '32px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '8px' }}>
                "The problem is often not a lack of information. The problem is having too much disconnected information and no structured system to follow."
              </h3>
              <p style={{ color: 'var(--fy-text-muted)', margin: 0, fontSize: '0.95rem' }}>
                Our 3-month program introduces that structured system so you can move forward with confidence.
              </p>
            </div>
          </div>
        </section>

        {/* 7. PRANAFLOW STUDENT DASHBOARD */}
        <section className="fy-section fy-dashboard-section">
          <div className="fy-container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-7">
                <span className="fy-dashboard-badge">PranaFlow LMS Student Portal</span>
                <h2 className="fy-serif-heading mb-3" style={{ color: '#ffffff', fontSize: '2rem' }}>Missed a Class? Your Learning Doesn't Stop.</h2>
                <p className="fy-hero-para" style={{ color: '#C3DFC8' }}>
                  Life happens. Work meetings, travel, family commitments, or unexpected situations should not break your momentum. After successful enrollment, you receive access to your personal <strong style={{ color: '#ffffff' }}>PranaFlow Student Dashboard</strong>.
                </p>

                <div className="row gy-2 mb-4" style={{ fontSize: '0.95rem', color: '#E8F5EB' }}>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Daily Lecture Information &amp; Schedule</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Available Lecture Recordings</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Missed-Class Catch-Up</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Revision Access During Program</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Downloadable Course Worksheets</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Program Progress Tracking</span></div></div>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--fy-radius-md)', padding: '20px 24px' }}>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fbbf24', margin: '0 0 6px' }}>
                    Learn Live. Catch Up When Needed. Revise When Required.
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#C3DFC8', lineHeight: 1.6 }}>
                    When an eligible session recording has been uploaded to your Student Dashboard, you can watch the missed class later to stay aligned with your batch.
                  </p>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="fy-dashboard-card">
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Your Student Dashboard Includes:</h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem', color: 'var(--fy-text-main)' }}>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Enrolled PCOS &amp; PCOD Program</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Daily Lecture Links &amp; Timings</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Structured 12-Module Content</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Uploaded Daily Session Videos</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Learning Resources &amp; Trackers</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Milestone Progress Tracking</span></li>
                  </ul>
                  <button type="button" className="fy-btn-primary w-100" onClick={() => scrollTo('enrollment')}>
                    Get Access Upon Enrollment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. MYTH-BUSTING */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">PCOS Myth-Busting</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '600px', margin: '12px auto 0' }}>
                Replacing internet confusion with medical and clinical clarity.
              </p>
            </div>

            <div className="row gy-3">
              {myths.map((item, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div className="fy-myth-card">
                    <span className="fy-myth-badge">MYTH vs FACT</span>
                    <h4 className="fy-myth-q">{item.m}</h4>
                    <p className="fy-myth-a"><strong>Fact:</strong> {item.f}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 9. WHY THIS PROGRAM IS DIFFERENT */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Why This Program Is Different</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-diff-grid">
              {diffPoints.map((card, idx) => (
                <div key={idx} className="fy-diff-card">
                  <h4 className="fy-diff-title">{card.title}</h4>
                  <p className="fy-diff-desc">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. 3-MONTH ROADMAP */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Your 3-Month Progression Journey</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-roadmap-grid">
              <div className="fy-roadmap-card month1">
                <div>
                  <span className="fy-roadmap-badge month1">Month 1</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Understand &amp; Build Foundation</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Week 1 — Understand Your PCOS</li>
                    <li>Week 2 — Nutrition Fundamentals</li>
                    <li>Week 3 — Practical Meal Planning</li>
                    <li>Week 4 — Metabolic Health &amp; Month 1 Review</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-sage-light)', border: '1px solid var(--fy-sage-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-sage-dark)', fontWeight: 600 }}>
                  Goal: Understand your condition &amp; build food habits.
                </div>
              </div>

              <div className="fy-roadmap-card month2">
                <div>
                  <span className="fy-roadmap-badge month2">Month 2</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Move, Restore &amp; Improve Routine</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Week 5 — Exercise &amp; Movement</li>
                    <li>Week 6 — Yoga &amp; Relaxation</li>
                    <li>Week 7 — Stress &amp; Emotional Wellbeing</li>
                    <li>Week 8 — Sleep &amp; Recovery Review</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-blush-light)', border: '1px solid var(--fy-blush-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-blush-dark)', fontWeight: 600 }}>
                  Goal: Realistic movement &amp; stress routine.
                </div>
              </div>

              <div className="fy-roadmap-card month3">
                <div>
                  <span className="fy-roadmap-badge month3">Month 3</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Cycle &amp; Long-Term Independence</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Week 9 — Menstrual &amp; Reproductive Health</li>
                    <li>Week 10 — Common PCOS Concerns &amp; Self-Care</li>
                    <li>Week 11 — Fertility, Tests &amp; Supplements</li>
                    <li>Week 12 — Long-Term Management Plan</li>
                  </ul>
                </div>
                <div style={{ background: '#DCF5E6', border: '1px solid #A7D3B6', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: '#1A5C33', fontWeight: 600 }}>
                  Goal: Consolidate habits for life.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 11. ENROLLMENT & SEAT RESERVATION */}
        <section id="pricing-form" className="fy-section">
          <div className="fy-container text-center">
            <div id="enrollment" className="py-2">
              <div className="fy-section-title-wrap text-center mb-3">
                <h2 className="fy-serif-heading" style={{ fontSize: '2rem' }}>
                  Course Enrollment &amp; Seat Reservation
                </h2>
                <div className="fy-title-underline"></div>
              </div>

              <p style={{ fontSize: '1.02rem', color: 'var(--fy-text-muted)', maxWidth: '880px', margin: '0 auto 36px', lineHeight: 1.75 }}>
                Our 3-Month PCOS &amp; PCOD Management Program provides a structured lifestyle, diet, and yoga routine to help you manage your health. Due to our commitment to individual attention, cohort sizes are strictly limited.
              </p>

              <div className="fy-enrollment-cta-group">
                <button
                  type="button"
                  className="fy-btn-primary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => setIsEnrollModalOpen(true)}
                >
                  Enroll Now &amp; Reserve Your Seat <i className="bi bi-arrow-right-circle-fill ms-1"></i>
                </button>

                <button
                  type="button"
                  className="fy-btn-secondary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => openModal(courseTitle, dbCourse)}
                >
                  Enquire Now <i className="bi bi-chat-dots ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 12. WHAT YOU RECEIVE */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <span className="fy-eyebrow">EVERYTHING INCLUDED</span>
              <h2 className="fy-serif-heading">What You Receive</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Everything included in the 3-Month PCOS &amp; PCOD Management Program from the moment you enroll.
              </p>
            </div>

            <div className="fy-receive-grid">
              {highlights.map((item, idx) => (
                <div key={idx} className="fy-receive-card">
                  <div className="fy-receive-icon">
                    <i className="bi bi-check-lg"></i>
                  </div>
                  <span className="fy-receive-text">{item}</span>
                </div>
              ))}
            </div>

            <div className="fy-receive-assurance">
              <i className="bi bi-shield-check"></i>
              <span>All resources, live sessions, and dashboard access are unlocked immediately upon registration.</span>
            </div>
          </div>
        </section>

        {/* 12.5 CERTIFICATE SECTION */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-7">
                <span className="fy-eyebrow">ACADEMY CERTIFICATION</span>
                <h2 className="fy-serif-heading mb-3">3-Month PCOS &amp; PCOD Management Program — Certificate of Completion</h2>
                <p className="fy-hero-para mb-3">
                  After successfully completing the <strong>3-Month PCOS &amp; PCOD Management Program</strong>, you will receive an official <strong>Certificate of Completion from PranaFlow Academy</strong>.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  The program provides structured education and guided practice covering yoga-based wellness, nutrition awareness, lifestyle management, stress-management techniques, movement, healthy routines, self-care practices, and sustainable wellness strategies related to PCOS and PCOD.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  To receive the certificate, participants are expected to complete the applicable training sessions, attendance requirements, learning activities, assignments, and final program requirements.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  Your certificate will confirm that you successfully completed the <strong>3-Month PCOS &amp; PCOD Management Program through PranaFlow Academy</strong>.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1.5rem' }}>
                  This is a <strong>wellness education and program-completion certificate</strong>. It does not provide a medical licence, authorization to diagnose or treat medical conditions, or any regulated healthcare professional qualification.
                </p>

                <div className="fy-trust-badges mt-2 mb-2" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', background: 'var(--fy-cream-card)', padding: '12px 20px', borderRadius: '30px', border: '1px solid var(--fy-sage-border)', fontSize: '0.88rem', fontWeight: '600', color: 'var(--fy-sage-dark)' }}>
                  <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '1.1rem' }}></i>
                  <span>3-Month Structured Program · Wellness Education · PranaFlow Academy Certificate of Completion</span>
                </div>
              </div>

              <div className="col-lg-5 text-center">
                <div style={{ background: 'var(--fy-cream-card)', border: '2px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '36px 28px', boxShadow: 'var(--fy-shadow-card)' }}>
                  <i className="bi bi-award-fill" style={{ fontSize: '3.8rem', color: 'var(--fy-sage-primary)' }}></i>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginTop: '16px', marginBottom: '8px' }}>
                    PCOS &amp; PCOD Program Certificate
                  </h3>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Certificate of Completion · PranaFlow Academy</div>
                  <p style={{ fontSize: '0.88rem', color: 'var(--fy-text-main)', lineHeight: 1.6, margin: 0 }}>
                    Awarded upon completing 3 months of structured wellness education, sessions, self-care routines, and program activities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 13. FAQ */}
        <section id="faq" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Frequently Asked Questions</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-faq-grid">
              {faqs.map((faq, idx) => (
                <div key={idx} className="fy-faq-item">
                  <button className="fy-faq-btn" aria-expanded={activeFaq === idx}
                    type="button"
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}>
                    <span>{faq.q}</span>
                    <div className="fy-faq-icon"><i className="bi bi-plus"></i></div>
                  </button>
                  {activeFaq === idx && (
                    <div className="fy-faq-ans" style={{ display: 'block' }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 15. FINAL CTA BANNER */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-cta-banner">
              <div className="row gy-4 align-items-center">
                <div className="col-lg-8">
                  <h2 className="fy-cta-heading">Stop Searching for the Perfect PCOS Hack.</h2>
                  <p className="fy-cta-sub">Start Building a Routine You Can Actually Follow.</p>
                  <p style={{ fontSize: '0.95rem', color: '#C3DFC8', lineHeight: 1.7, marginBottom: '20px' }}>
                    For the next 3 months, work on your nutrition, movement, yoga, sleep, stress, menstrual health awareness, and long-term lifestyle plan through one organized program managed in your PranaFlow Student Dashboard.
                  </p>
                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary fy-cta-btn-white" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary fy-cta-btn-outline" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                  </div>
                </div>
                <div className="col-lg-4 text-center">
                  <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 20px' }}>
                    <div className="fy-cta-logo-wrap">
                      <img 
                        src={getLogoUrl()} 
                        alt="PranaFlow Academy" 
                        className="fy-cta-card-logo"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/apple-touch-icon.png'; }}
                      />
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#ffffff', marginBottom: '4px' }}>3 Months</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>12 Structured Weeks</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>Up to 72 Guided Sessions</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 16. HEALTH NOTICE */}
        <div className="fy-container">
          <div className="fy-page-disclaimer">
            <strong>Important Health Notice:</strong> This program is designed for education, wellness and lifestyle support. It does not diagnose, treat or cure PCOS or any other medical condition and does not replace individualized care from a doctor, gynecologist, endocrinologist, registered dietitian or other appropriately qualified healthcare professional. Participants should continue prescribed medications and existing medical treatment unless their treating healthcare professional advises otherwise. People who are pregnant, trying to conceive, receiving fertility treatment, taking medication, managing diabetes or another medical condition, or experiencing severe or unusual symptoms should discuss participation and relevant lifestyle changes with their healthcare professional. Individual experiences and outcomes vary.
          </div>
        </div>

      </main>

      <CourseEnrollModal
        isOpen={isEnrollModalOpen}
        onClose={() => setIsEnrollModalOpen(false)}
        courseTitle={courseTitle}
        formData={formData}
        setFormData={setFormData}
        upcomingBatches={upcomingBatches}
        isSubmitting={isSubmitting}
        isSuccess={isSuccess}
        handleEnrollSubmit={handleEnrollSubmit}
      />

      <PublicFooter />
    </div>
  );
};

export default PCOSManagementProgram;
